export interface DefaultQuery {
  /** Metric keys to pre-select */
  metrics: string[];
  /** Dimension keys to pre-select */
  dimensions: string[];
}

/**
 * Registry of default queries per measurement path.
 * When a user's first selection triggers a measurement being set,
 * these defaults are auto-applied to give a useful starting point.
 *
 * For NMS measurements, paths are prefixed with /metrics/ at runtime.
 * Use the full runtime path (e.g., '/metrics/interfaces/counters').
 */
const DEFAULT_QUERIES: Record<string, DefaultQuery> = {
  // --- Traffic ---
  '/traffic': {
    metrics: ['both_bits_per_sec'],
    dimensions: ['i_device_id']
  },

  // --- Synthetics ---
  '/synthetics/ping': {
    metrics: ['ping_latency_avg'],
    dimensions: ['src_agent', 'dst_task_target']
  },
  '/synthetics/dns': {
    metrics: ['dns_latency_avg'],
    dimensions: ['src_agent', 'task_dns_target_hostname']
  },
  '/synthetics/throughput': {
    metrics: ['throughput_bandwidth_avg'],
    dimensions: ['src_agent', 'dst_task_target']
  },
  '/synthetics/http': {
    metrics: ['http_latency_avg'],
    dimensions: ['src_agent', 'dst_task_target']
  },
  '/synthetics/pageload': {
    metrics: ['pageload_latency_avg'],
    dimensions: ['src_agent', 'dst_task_target']
  },

  // --- Events ---
  '/events/event_syslog': {
    metrics: ['ktappprotocol__event_syslog__i_count'],
    dimensions: [
      'ktappprotocol__event_syslog__i_device_name',
      'ktappprotocol__event_syslog__INT06',
      'ktappprotocol__event_syslog__STR05'
    ]
  },
  '/events/event_snmp_traps': {
    metrics: ['ktappprotocol__event_snmp_traps__i_count'],
    dimensions: ['ktappprotocol__event_snmp_traps__i_device_name', 'ktappprotocol__event_snmp_traps__INT00']
  },
  '/events/event_deviceconf': {
    metrics: ['ktappprotocol__event_deviceconf__i_count'],
    dimensions: [
      'ktappprotocol__event_deviceconf__i_device_name',
      'ktappprotocol__event_deviceconf__STR03',
      'ktappprotocol__event_deviceconf__STR02',
      'ktappprotocol__event_deviceconf__INT64_02'
    ]
  },

  // --- NMS ---
  '/metrics/interfaces/counters': {
    metrics: ['in-utilization'],
    dimensions: ['device_name', 'name']
  },
  '/metrics/system': {
    metrics: ['status'],
    dimensions: ['device_name']
  },
  '/metrics/system/cpus': {
    metrics: ['total/instant'],
    dimensions: ['device_name', 'name']
  },
  '/metrics/system/memory': {
    metrics: ['utilization'],
    dimensions: ['device_name']
  },
  '/metrics/protocols/bgp/neighbors': {
    metrics: ['session-state'],
    dimensions: ['device_name', 'transport/remote-address']
  },
  '/metrics/components': {
    metrics: ['oper-status'],
    dimensions: ['device_name', 'name']
  }
};

/**
 * Look up the default query for a measurement path.
 * Returns undefined if no defaults are defined.
 */
export function getDefaultQuery(measurementPath: string): DefaultQuery | undefined {
  return DEFAULT_QUERIES[measurementPath];
}
